import { useState, useEffect, useRef } from 'react';
import {
  listOrgCollection, readOrgFile, writeOrgFile, deleteOrgFile, orgBinaryUrl,
} from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EntitySelector from '../shared/EntitySelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import InfoBadge from '../shared/InfoBadge';
import { BG_SUBTLE, BG_HOVER_LT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, STATUS_GOOD, BG_SHELL, STATUS_NEUTRAL, FIELD_ERROR_BORDER, STATUS_WARN, UNSAVED_AMBER, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM, FONT_STAT_VALUE } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import { SUPPORTED_NORMS } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:              'Métricas y KPIs',
    newBtn:             '+ Nueva métrica',
    save:               'Guardar',
    saving:             'Guardando…',
    saveOk:             '✓ Guardado',
    saveError:          'Error al guardar',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    loading:            'Cargando…',
    emptyTitle:         'No hay métricas registradas.',
    emptyHint:          'Crea la primera métrica con el botón "+ Nueva métrica".',
    deleteBtn:          'Eliminar',
    deleteFormBtn:      'Eliminar métrica',
    deleteConfirm:      '¿Eliminar la métrica "{ref}"? Esta acción no se puede deshacer.',
    deleteError:        'Error al eliminar. Inténtalo de nuevo.',
    formSave:           'Guardar métrica',
    validationError:    'Por favor completa los campos obligatorios marcados con *.',
    formTitleNew:       'Nueva métrica',
    formTitleEdit:      'Editar métrica',
    formTitleView:      'Ver métrica',
    backToList:         '← Volver a la lista',
    // summary
    cntTotal:           'Total',
    cntMet:             'Cumplido',
    cntAtRisk:          'En riesgo',
    cntNotMet:          'No cumplido',
    cntNoData:          'Sin datos',
    // table
    colRef:             'Referencia',
    colName:            'Nombre',
    colFrequency:       'Frecuencia',
    colResponsible:     'Responsable',
    colTarget:          'Meta',
    colLatestReading:   'Última lectura',
    colRag:             'Estado',
    colActions:         'Acciones',
    viewBtn:            'Ver',
    editBtn:            'Editar',
    // filters
    filterFrequency:    'Frecuencia',
    filterRag:          'Estado RAG',
    filterAll:          'Todos',
    // sections
    sectionA:           'Definición de la métrica',
    sectionB:           'Relaciones',
    sectionC:           'Lecturas',
    // fields
    fRef:               'Referencia',
    fName:              'Nombre',
    fDescription:       'Descripción',
    fMeasurementMethod: 'Método de medición',
    fDataSource:        'Fuente de datos',
    fUnit:              'Unidad',
    fFrequency:         'Frecuencia',
    fResponsible:       'Responsable',
    fTarget:            'Meta',
    fAlertThreshold:    'Umbral de alerta',
    fDefineRag:         'Definir umbrales RAG',
    fRagDirection:     'Sentido del indicador',
    directionHigher:   'Más es mejor',
    directionLower:    'Menos es mejor',
    ttRagDirection:    'Determina cómo se interpretan los umbrales. "Más es mejor" para indicadores que crecen al mejorar (cobertura, % de efectividad). "Menos es mejor" para indicadores que decrecen al mejorar (hallazgos abiertos, incidentes, tiempo de resolución).',
    fRagGreen:          'Verde (en objetivo)',
    fRagAmber:          'Ámbar (en riesgo)',
    fRagRed:            'Rojo (no cumplido)',
    fObjective:         'KPI',
    ttObjective:        'Key Performance Indicator: métrica que mide el desempeño de un objetivo.',
    fLinkedControls:    'Controles vinculados del Anexo A',
    fLinkedRisks:       'KRI',
    ttLinkedRisks:      'Key Risk Indicator: métrica que monitoriza la evolución de un riesgo.',
    objPlaceholder:     'Buscar objetivo…',
    ctrlPlaceholder:    'Buscar control…',
    riskPlaceholder:    'Buscar riesgo…',
    // readings
    readingsEmpty:      'Sin lecturas registradas.',
    addReadingTitle:    'Añadir lectura',
    fReadingDate:       'Fecha',
    fReadingValue:      'Valor',
    fReadingStatus:     'Estado',
    fReadingEvidenceId: 'ID de evidencia',
    fReadingNotes:      'Notas',
    addReadingBtn:      'Añadir',
    deleteReadingBtn:   'Eliminar',
    colReadingDate:     'Fecha',
    colReadingValue:    'Valor',
    colReadingStatus:   'Estado',
    colReadingEvidence: 'Evidencia',
    colReadingNotes:    'Notas',
    // status / rag labels
    statusMet:          'Cumplido',
    statusPartial:      'Parcial',
    statusNotMet:       'No cumplido',
    ragGreen:           'En objetivo',
    ragAmber:           'En riesgo',
    ragRed:             'No cumplido',
    ragNoData:          'Sin datos',
    // frequency labels
    freqWeekly:         'Semanal',
    freqMonthly:        'Mensual',
    freqQuarterly:      'Trimestral',
    freqAnnual:         'Anual',
    required:           '*',
    requiredLegend:     '* Campos obligatorios',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:              'Metrics and KPIs',
    newBtn:             '+ New metric',
    save:               'Save',
    saving:             'Saving…',
    saveOk:             '✓ Saved',
    saveError:          'Save error',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    loading:            'Loading…',
    emptyTitle:         'No metrics recorded yet.',
    emptyHint:          'Create the first metric using the "+ New metric" button.',
    deleteBtn:          'Delete',
    deleteFormBtn:      'Delete metric',
    deleteConfirm:      'Delete metric "{ref}"? This cannot be undone.',
    deleteError:        'Delete failed. Please try again.',
    formSave:           'Save metric',
    validationError:    'Please complete the required fields marked with *.',
    formTitleNew:       'New metric',
    formTitleEdit:      'Edit metric',
    formTitleView:      'View metric',
    backToList:         '← Back to list',
    // summary
    cntTotal:           'Total',
    cntMet:             'Met',
    cntAtRisk:          'At Risk',
    cntNotMet:          'Not Met',
    cntNoData:          'No Data',
    // table
    colRef:             'Ref',
    colName:            'Name',
    colFrequency:       'Frequency',
    colResponsible:     'Responsible',
    colTarget:          'Target',
    colLatestReading:   'Latest reading',
    colRag:             'Status',
    colActions:         'Actions',
    viewBtn:            'View',
    editBtn:            'Edit',
    // filters
    filterFrequency:    'Frequency',
    filterRag:          'RAG status',
    filterAll:          'All',
    // sections
    sectionA:           'Metric definition',
    sectionB:           'Relationships',
    sectionC:           'Readings',
    // fields
    fRef:               'Ref',
    fName:              'Name',
    fDescription:       'Description',
    fMeasurementMethod: 'Measurement method',
    fDataSource:        'Data source',
    fUnit:              'Unit',
    fFrequency:         'Frequency',
    fResponsible:       'Responsible',
    fTarget:            'Target',
    fAlertThreshold:    'Alert threshold',
    fDefineRag:         'Define RAG thresholds',
    fRagDirection:     'Direction',
    directionHigher:   'Higher is better',
    directionLower:    'Lower is better',
    ttRagDirection:    'Determines how thresholds are interpreted. "Higher is better" for indicators that grow as they improve (coverage, % effectiveness). "Lower is better" for indicators that decrease as they improve (open findings, incidents, time to resolution).',
    fRagGreen:          'Green (on target)',
    fRagAmber:          'Amber (at risk)',
    fRagRed:            'Red (not met)',
    fObjective:         'KPI',
    ttObjective:        'Key Performance Indicator: metric that measures objective performance.',
    fLinkedControls:    'Annex A Linked Controls',
    fLinkedRisks:       'KRI',
    ttLinkedRisks:      'Key Risk Indicator: metric that monitors how a risk evolves.',
    objPlaceholder:     'Search objective…',
    ctrlPlaceholder:    'Search control…',
    riskPlaceholder:    'Search risk…',
    // readings
    readingsEmpty:      'No readings recorded.',
    addReadingTitle:    'Add reading',
    fReadingDate:       'Date',
    fReadingValue:      'Value',
    fReadingStatus:     'Status',
    fReadingEvidenceId: 'Evidence ID',
    fReadingNotes:      'Notes',
    addReadingBtn:      'Add',
    deleteReadingBtn:   'Delete',
    colReadingDate:     'Date',
    colReadingValue:    'Value',
    colReadingStatus:   'Status',
    colReadingEvidence: 'Evidence',
    colReadingNotes:    'Notes',
    // status / rag labels
    statusMet:          'Met',
    statusPartial:      'Partial',
    statusNotMet:       'Not Met',
    ragGreen:           'On target',
    ragAmber:           'At risk',
    ragRed:             'Not met',
    ragNoData:          'No data',
    // frequency labels
    freqWeekly:         'Weekly',
    freqMonthly:        'Monthly',
    freqQuarterly:      'Quarterly',
    freqAnnual:         'Annual',
    required:           '*',
    requiredLegend:     '* Required fields',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const FREQUENCIES = ['Weekly', 'Monthly', 'Quarterly', 'Annual'];
const READING_STATUSES = ['Met', 'Not Met', 'Partial'];

const RAG_COLORS = {
  green: { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK, border: '#86efac' },
  amber: { bg: '#fffbeb', text: STATUS_WARN.text, border: '#fcd34d' },
  red:   { bg: ERROR_BG, text: ERROR_TEXT, border: ERROR_BORDER },
  none:  { bg: BG_SUBTLE,  text: TEXT_MUTED, border: BORDER_DEFAULT },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
async function genRef(orgId, metricPrefix) {
  const now = new Date();
  const year = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyymmdd = `${year}${mm}${dd}`;
  const prefix = `${metricPrefix}-${year}-`;
  try {
    const items = await listOrgCollection(orgId, 'metrics');
    let max = 0;
    items.forEach(m => {
      if (m.ref && m.ref.startsWith(prefix)) {
        const n = parseInt(m.ref.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    });
    const seq = String(max + 1).padStart(3, '0');
    return { ref: `${prefix}${seq}`, fileId: `met-${yyyymmdd}-${seq}` };
  } catch {
    return { ref: `${prefix}001`, fileId: `met-${yyyymmdd}-001` };
  }
}

function computeRagStatus(met) {
  const readings = Array.isArray(met.readings) ? met.readings : [];
  if (readings.length === 0) return 'none';
  const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  const latest = sorted[0];
  if (met.ragThresholds) {
    const val = parseFloat(latest.value);
    const green = parseFloat(met.ragThresholds.green);
    const amber = parseFloat(met.ragThresholds.amber);
    const direction = (met.ragThresholds && met.ragThresholds.direction) || 'higher-is-better';
    if (!isNaN(val) && !isNaN(green) && !isNaN(amber)) {
      if (direction === 'lower-is-better') {
        if (val <= green) return 'green';
        if (val <= amber) return 'amber';
        return 'red';
      }
      if (val >= green) return 'green';
      if (val >= amber) return 'amber';
      return 'red';
    }
  }
  if (latest.status === 'Met') return 'green';
  if (latest.status === 'Partial') return 'amber';
  if (latest.status === 'Not Met') return 'red';
  return 'none';
}

function getLatestReading(met) {
  const readings = Array.isArray(met.readings) ? met.readings : [];
  if (readings.length === 0) return null;
  return readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
}

function blankForm(ref, fileId) {
  return {
    _fileId: fileId,
    ref,
    name: '',
    description: '',
    measurementMethod: '',
    dataSource: '',
    unit: '',
    frequency: 'Monthly',
    responsible: '',
    target: '',
    alertThreshold: '',
    defineRag: false,
    ragGreen: '',
    ragAmber: '',
    ragRed: '',
    ragDirection: 'higher-is-better',
    objectiveId: null,
    linkedControlIds: [],
    linkedRiskIds: [],
    readings: [],
    newReadingDate: '',
    newReadingValue: '',
    newReadingStatus: 'Met',
    newReadingEvidenceId: '',
    newReadingNotes: '',
  };
}

function metToForm(met) {
  return {
    _fileId: met.id,
    ref:               met.ref || '',
    name:              met.name || '',
    description:       met.description || '',
    measurementMethod: met.measurementMethod || '',
    dataSource:        met.dataSource || '',
    unit:              met.unit || '',
    frequency:         met.frequency || 'Monthly',
    responsible:       met.responsible || '',
    target:            met.target || '',
    alertThreshold:    met.alertThreshold || '',
    defineRag:         !!(met.ragThresholds),
    ragGreen:          met.ragThresholds ? met.ragThresholds.green : '',
    ragAmber:          met.ragThresholds ? met.ragThresholds.amber : '',
    ragRed:            met.ragThresholds ? met.ragThresholds.red : '',
    ragDirection:      (met.ragThresholds && met.ragThresholds.direction) || 'higher-is-better',
    objectiveId:       met.objectiveId || null,
    linkedControlIds:  Array.isArray(met.linkedControlIds) ? [...met.linkedControlIds] : [],
    linkedRiskIds:     Array.isArray(met.linkedRiskIds) ? [...met.linkedRiskIds] : [],
    readings:          Array.isArray(met.readings) ? [...met.readings] : [],
    newReadingDate:    '',
    newReadingValue:   '',
    newReadingStatus:  'Met',
    newReadingEvidenceId: '',
    newReadingNotes:   '',
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL, fontSize: '0.85rem',
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL, fontSize: '0.7rem',
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const thSt = {
  ...FONT_TINY, fontSize: '0.67rem',
  color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase',
  padding: '0.55rem 0.7rem', textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`, background: BG_SUBTLE,
  whiteSpace: 'nowrap',
};

const tdSt = {
  ...FONT_LIST_ITEM, fontSize: '0.84rem',
  color: TEXT_DEFAULT, padding: '0.65rem 0.7rem',
  borderBottom: `1px solid ${BG_SHELL}`, verticalAlign: 'middle',
};

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem',
  paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.5rem 0' }} />;
}

function RagBadge({ rag, t }) {
  const colors = RAG_COLORS[rag] || RAG_COLORS.none;
  const label =
    rag === 'green' ? t.ragGreen :
    rag === 'amber' ? t.ragAmber :
    rag === 'red'   ? t.ragRed   : t.ragNoData;
  return (
    <span style={{
      ...FONT_SMALL, fontSize: '0.7rem', fontWeight: 600,
      background: colors.bg, color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: 5, padding: '0.2rem 0.55rem',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function ReadingStatusBadge({ status, t }) {
  const colors =
    status === 'Met'     ? { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK } :
    status === 'Partial' ? { bg: '#fffbeb', text: STATUS_WARN.text } :
                           { bg: ERROR_BG, text: ERROR_TEXT };
  const label =
    status === 'Met'     ? t.statusMet :
    status === 'Partial' ? t.statusPartial : t.statusNotMet;
  return (
    <span style={{
      ...FONT_SMALL, fontSize: '0.7rem', fontWeight: 600,
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.55rem',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function MetricsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [metrics, setMetrics]         = useState(null);
  const [people, setPeople]           = useState([]);
  const [objectives, setObjectives]   = useState([]);
  const [soaControls, setSoaControls] = useState([]);
  const [risks, setRisks]             = useState([]);
  const [evidences, setEvidences]     = useState([]);

  const [formSaveState, setFormSaveState] = useState('idle');
  const [formOpen, setFormOpen]           = useState(false);
  const [formMode, setFormMode]           = useState('edit');
  const [editingId, setEditingId]         = useState(null);
  const [form, setForm]                   = useState(null);
  const [formErrors, setFormErrors]       = useState({});
  const [dirty, setDirty]                 = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef                 = useRef(false);
  const lastProcessedAppTickRef           = useRef(0);

  const [confirmState, setConfirmState] = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);
  const [deleteError, setDeleteError]   = useState(null);

  const [filterFreq, setFilterFreq] = useState('');
  const [filterRag,  setFilterRag]  = useState('');

  useScrollToTopOnFormOpen(editingId !== null);

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'metrics')
      .then(items => setMetrics([...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''))))
      .catch(() => setMetrics([]));
  }, [org.id]);

  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(d => setPeople(Array.isArray(d.people) ? d.people : []))
      .catch(() => setPeople([]));
    listOrgCollection(org.id, 'objectives')
      .then(items => setObjectives(items))
      .catch(() => setObjectives([]));
    readOrgFile(org.id, 'controls/soa.json')
      .then(soa => setSoaControls((soa && soa.controls ? soa.controls : []).filter(c => c.applicable)))
      .catch(() => setSoaControls([]));
    listOrgCollection(org.id, 'risks')
      .then(items => setRisks(items))
      .catch(() => setRisks([]));
    listOrgCollection(org.id, 'evidence')
      .then(items => setEvidences(items))
      .catch(() => setEvidences([]));
  }, [org.id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const setF = (field, val) => {
    if (formMode !== 'view') setDirty(true);
    setForm(prev => prev ? { ...prev, [field]: val } : prev);
  };

  const openEvidenceFile = async (filePath) => {
    if (!filePath) return;
    const url = await orgBinaryUrl(org.id, filePath);
    if (!url) return;
    window.open(url, '_blank');
    setTimeout(() => { URL.revokeObjectURL(url); }, 60000);
  };

  const renderEvidenceCell = (r) => {
    if (!r.evidenceId) return '—';
    const ev = evidences.find(e => e.id === r.evidenceId);
    if (!ev) return r.evidenceId;
    const label = ev.ref;
    const tooltip = ev.title || ev.ref;
    if (!ev.filePath) return label;
    return (
      <button
        type="button"
        onClick={() => openEvidenceFile(ev.filePath)}
        title={tooltip}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: BRAND_PRIMARY,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        }}
      >
        {label}
      </button>
    );
  };

  const clearErr = (field) => {
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const freqLabel = (f) => ({
    Weekly: t.freqWeekly, Monthly: t.freqMonthly,
    Quarterly: t.freqQuarterly, Annual: t.freqAnnual,
  }[f] || f);

  // ── App-level guard: bridge App tick into local tick (which the existing
  //    saveRequestTick watcher below picks up to call handleFormSave).
  //    See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formOpen) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload ──────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Save request watcher ──────────────────────────────────────────────────
  useEffect(() => {
    if (saveRequestTick > 0) { handleFormSave(); setSaveRequestTick(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Form open/close ───────────────────────────────────────────────────────
  const openNew = async () => {
    const metricPrefix = (org.refPrefixes && org.refPrefixes.metric) || 'met';
    const { ref, fileId } = await genRef(org.id, metricPrefix);
    setForm(blankForm(ref, fileId));
    setFormMode('create');
    setEditingId(null);
    setFormErrors({});
    setDirty(false);
    setFormOpen(true);
  };

  const openEdit = (met) => {
    setForm(metToForm(met));
    setFormMode('edit');
    setEditingId(met.id);
    setFormErrors({});
    setDirty(false);
    setFormOpen(true);
  };

  const openView = (met) => {
    setForm(metToForm(met));
    setFormMode('view');
    setEditingId(met.id);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setDirty(false);
    setFormOpen(false);
    setForm(null);
    setFormErrors({});
  };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => { setConfirmState(null); setSaveRequestTick(n => n + 1); },
        },
      });
      return;
    }
    closeForm();
  };

  // ── Form save ─────────────────────────────────────────────────────────────
  const handleFormSave = async () => {
    const errs = {};
    if (!form.ref?.trim())               errs.ref = true;
    if (!form.name?.trim())              errs.name = true;
    if (!form.measurementMethod?.trim()) errs.measurementMethod = true;
    if (!form.frequency)                 errs.frequency = true;
    if (!form.responsible?.trim())       errs.responsible = true;
    if (!form.target?.trim())            errs.target = true;
    if (form.defineRag) {
      if (!form.ragGreen?.trim()) errs.ragGreen = true;
      if (!form.ragAmber?.trim()) errs.ragAmber = true;
      if (!form.ragRed?.trim())   errs.ragRed   = true;
    }

    if (Object.keys(errs).length > 0) { setFormErrors(errs); return; }

    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    setFormSaveState('saving');

    try {
      let fileId;
      let obj;
      let previousObjectiveId = null;

      if (editingId === null) {
        fileId = form._fileId;
        obj = {
          id:                fileId,
          orgId:             org.id,
          ref:               form.ref.trim(),
          norms:             org.norms || [SUPPORTED_NORMS[0]],
          objectiveId:       form.objectiveId || null,
          linkedControlIds:  form.linkedControlIds,
          linkedRiskIds:     form.linkedRiskIds,
          name:              form.name.trim(),
          description:       form.description.trim() || null,
          measurementMethod: form.measurementMethod.trim(),
          dataSource:        form.dataSource.trim() || null,
          unit:              form.unit.trim() || null,
          frequency:         form.frequency,
          responsible:       form.responsible,
          target:            form.target.trim(),
          alertThreshold:    form.alertThreshold.trim() || null,
          ragThresholds:     form.defineRag
            ? { direction: form.ragDirection, green: form.ragGreen.trim(), amber: form.ragAmber.trim(), red: form.ragRed.trim() }
            : null,
          readings:          form.readings,
          changeLog:         [],
          updatedAt:         now,
        };
      } else {
        fileId = editingId;
        const existing = (metrics || []).find(m => m.id === editingId) || {};
        previousObjectiveId = existing.objectiveId || null;
        obj = {
          ...existing,
          id:                fileId,
          orgId:             org.id,
          ref:               form.ref.trim(),
          norms:             existing.norms || org.norms || [SUPPORTED_NORMS[0]],
          objectiveId:       form.objectiveId || null,
          linkedControlIds:  form.linkedControlIds,
          linkedRiskIds:     form.linkedRiskIds,
          name:              form.name.trim(),
          description:       form.description.trim() || null,
          measurementMethod: form.measurementMethod.trim(),
          dataSource:        form.dataSource.trim() || null,
          unit:              form.unit.trim() || null,
          frequency:         form.frequency,
          responsible:       form.responsible,
          target:            form.target.trim(),
          alertThreshold:    form.alertThreshold.trim() || null,
          ragThresholds:     form.defineRag
            ? { direction: form.ragDirection, green: form.ragGreen.trim(), amber: form.ragAmber.trim(), red: form.ragRed.trim() }
            : null,
          readings:          form.readings,
          updatedAt:         now,
        };
        obj.changeLog = appendChangeLog(existing, obj, changedBy);
      }

      await writeOrgFile(org.id, `metrics/${fileId}.json`, obj);

      // Bidirectional sync — update objective's linkedMetricIds
      const newObjectiveId = form.objectiveId || null;
      if (previousObjectiveId !== newObjectiveId) {
        if (previousObjectiveId) {
          try {
            const prevObj = await readOrgFile(org.id, `objectives/${previousObjectiveId}.json`);
            const prevLinkedIds = Array.isArray(prevObj.linkedMetricIds) ? prevObj.linkedMetricIds : [];
            if (prevLinkedIds.includes(fileId)) {
              await writeOrgFile(org.id, `objectives/${previousObjectiveId}.json`, {
                ...prevObj, linkedMetricIds: prevLinkedIds.filter(id => id !== fileId), updatedAt: now,
              });
            }
          } catch {
            // non-fatal
          }
        }
        if (newObjectiveId) {
          try {
            const newObj = await readOrgFile(org.id, `objectives/${newObjectiveId}.json`);
            const newLinkedIds = Array.isArray(newObj.linkedMetricIds) ? newObj.linkedMetricIds : [];
            if (!newLinkedIds.includes(fileId)) {
              await writeOrgFile(org.id, `objectives/${newObjectiveId}.json`, {
                ...newObj, linkedMetricIds: [...newLinkedIds, fileId], updatedAt: now,
              });
            }
          } catch {
            // non-fatal
          }
        }
      }

      const items = await listOrgCollection(org.id, 'metrics');
      setMetrics([...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || '')));

      if (editingId === null) { setEditingId(fileId); setFormMode('edit'); }
      setDirty(false);
      setFormErrors({});
      setFormSaveState('ok');
      setTimeout(() => {
        setFormSaveState('idle');
      }, 2000);
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setFormSaveState('error');
      setTimeout(() => setFormSaveState('idle'), 3000);
    }
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteOne = async (met) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'metric', met.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', met.ref || met.id),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `metrics/${met.id}.json`);
            setMetrics(prev => (prev || []).filter(m => m.id !== met.id));
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteFromForm = async () => {
    if (editingId === null) return;
    const met = (metrics || []).find(m => m.id === editingId) || { id: editingId, ref: form?.ref };
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'metric', met.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', met.ref || met.id),
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `metrics/${met.id}.json`);
            setMetrics(prev => (prev || []).filter(m => m.id !== met.id));
            closeForm();
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Add reading ───────────────────────────────────────────────────────────
  const handleAddReading = () => {
    if (!form.newReadingDate || !form.newReadingValue.trim()) return;
    const newReading = {
      date:       form.newReadingDate,
      value:      form.newReadingValue.trim(),
      status:     form.newReadingStatus,
      evidenceId: form.newReadingEvidenceId || null,
      notes:      form.newReadingNotes.trim(),
    };
    setF('readings', [...form.readings, newReading]);
    setForm(prev => ({
      ...prev,
      newReadingDate: '', newReadingValue: '', newReadingStatus: 'Met',
      newReadingEvidenceId: '', newReadingNotes: '',
    }));
    listOrgCollection(org.id, 'evidence').then(items => setEvidences(items)).catch(() => {});
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (metrics === null) {
    return (
      <div style={{ ...FONT_LABEL, fontSize: '0.85rem', padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const total       = metrics.length;
  const metCount    = metrics.filter(m => computeRagStatus(m) === 'green').length;
  const atRiskCount = metrics.filter(m => computeRagStatus(m) === 'amber').length;
  const notMetCount = metrics.filter(m => computeRagStatus(m) === 'red').length;
  const noDataCount = metrics.filter(m => computeRagStatus(m) === 'none').length;

  const filteredMetrics = metrics.filter(m => {
    if (filterFreq && m.frequency !== filterFreq) return false;
    if (filterRag  && computeRagStatus(m) !== filterRag) return false;
    return true;
  });

  const objectiveItems = objectives.map(o => ({
    id: o.id,
    label: `${o.ref || o.id} — ${(o.objective || '').slice(0, 50)}`,
  }));
  const controlItems = soaControls.map(c => ({
    id: c.id,
    label: `A.${c.id} — ${(c.title || '').slice(0, 50)}`,
  }));
  const riskItems = risks.map(r => ({
    id: r.id,
    label: `${r.ref || r.id} — ${(r.title || r.description || '').slice(0, 50)}`,
  }));

  // ── FORM VIEW ─────────────────────────────────────────────────────────────
  if (formOpen && form) {
    const isView = formMode === 'view';
    const sortedReadings = [...form.readings].sort((a, b) => new Date(b.date) - new Date(a.date));
    const hasErrors = Object.keys(formErrors).length > 0;

    const cardSt = {
      background: BG_SURFACE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
      borderRadius: 10, padding: '1.5rem 1.75rem',
    };

    return (
      <div>
        {/* Form header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem',
        }}>
          <div>
            <button
              onClick={handleFormCancel}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', cursor: 'pointer',
                color: BRAND_PRIMARY, padding: 0, display: 'block', marginBottom: '0.5rem',
              }}
            >{t.backToList}</button>
            <h2 style={{
              ...FONT_PAGE_TITLE, fontSize: '1.4rem',
              color: TEXT_HEADING, margin: 0,
            }}>
              {isView
                ? `${t.formTitleView}: ${form.ref}`
                : editingId === null ? t.formTitleNew : `${t.formTitleEdit}: ${form.ref}`}
            </h2>
          </div>
        </div>

        {!isView && (
          <p style={{ ...FONT_SMALL, fontSize: '0.7rem', color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
        )}

        {formSaveState === 'error' && (
          <div style={{
            ...FONT_LABEL,
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.saveError}
          </div>
        )}

        {hasErrors && (
          <div style={{
            ...FONT_LABEL, fontSize: '0.82rem',
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.validationError}
          </div>
        )}

        <fieldset disabled={isView || formSaveState === 'saving'} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* ── Section A — Metric definition ── */}
            <div style={cardSt}>
              <div style={sectionHeading}>{t.sectionA}</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fRef} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <input
                    type="text" value={form.ref}
                    onChange={e => { setF('ref', e.target.value); clearErr('ref'); }}
                    style={inputSt(formErrors.ref)}
                  />
                </div>
                <div>
                  <label style={labelSt}>{t.fFrequency} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <select
                    value={form.frequency}
                    onChange={e => { setF('frequency', e.target.value); clearErr('frequency'); }}
                    style={inputSt(formErrors.frequency)}
                  >
                    {FREQUENCIES.map(f => (<option key={f} value={f}>{freqLabel(f)}</option>))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={form.name}
                  onChange={e => { setF('name', e.target.value); clearErr('name'); }}
                  style={inputSt(formErrors.name)}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fDescription}</label>
                <textarea
                  rows={2} value={form.description}
                  onChange={e => setF('description', e.target.value)}
                  style={{ ...inputSt(false), resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fMeasurementMethod} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <textarea
                  rows={2} value={form.measurementMethod}
                  onChange={e => { setF('measurementMethod', e.target.value); clearErr('measurementMethod'); }}
                  style={{ ...inputSt(formErrors.measurementMethod), resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fDataSource}</label>
                  <input
                    type="text" value={form.dataSource}
                    onChange={e => setF('dataSource', e.target.value)}
                    style={inputSt(false)}
                  />
                </div>
                <div>
                  <label style={labelSt}>{t.fUnit}</label>
                  <input
                    type="text" value={form.unit}
                    onChange={e => setF('unit', e.target.value)}
                    style={inputSt(false)}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={labelSt}>{t.fResponsible} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <PeopleSelector
                    orgId={org.id} lang={lang}
                    value={form.responsible || ''}
                    onChange={val => { setF('responsible', val); clearErr('responsible'); }}
                    disabled={formSaveState === 'saving'}
                  />
                  {formErrors.responsible && (
                    <span style={{ ...FONT_TINY, fontSize: '0.68rem', color: ERROR_TEXT }}>
                      {t.validationError}
                    </span>
                  )}
                </div>
                <div>
                  <label style={labelSt}>{t.fTarget} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                  <input
                    type="text" value={form.target}
                    onChange={e => { setF('target', e.target.value); clearErr('target'); }}
                    style={inputSt(formErrors.target)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fAlertThreshold}</label>
                <input
                  type="text" value={form.alertThreshold}
                  onChange={e => setF('alertThreshold', e.target.value)}
                  style={inputSt(false)}
                />
              </div>

              {/* RAG thresholds */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={form.defineRag}
                    onChange={e => setF('defineRag', e.target.checked)}
                  />
                  <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
                    {t.fDefineRag}
                  </span>
                </label>
                {form.defineRag && (
                  <>
                    <div style={{ paddingLeft: '0.5rem', marginBottom: '0.75rem' }}>
                      <label style={labelSt}>{t.fRagDirection} <InfoBadge text={t.ttRagDirection} /></label>
                      <select value={form.ragDirection} onChange={e => setF('ragDirection', e.target.value)} style={inputSt(false)}>
                        <option value="higher-is-better">{t.directionHigher}</option>
                        <option value="lower-is-better">{t.directionLower}</option>
                      </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', paddingLeft: '0.5rem' }}>
                      <div>
                        <label style={{ ...labelSt, color: BRAND_PRIMARY_DARK }}>{t.fRagGreen} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                        <input
                          type="text" value={form.ragGreen}
                          onChange={e => { setF('ragGreen', e.target.value); clearErr('ragGreen'); }}
                          style={inputSt(formErrors.ragGreen)}
                        />
                      </div>
                      <div>
                        <label style={{ ...labelSt, color: STATUS_WARN.text }}>{t.fRagAmber} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                        <input
                          type="text" value={form.ragAmber}
                          onChange={e => { setF('ragAmber', e.target.value); clearErr('ragAmber'); }}
                          style={inputSt(formErrors.ragAmber)}
                        />
                      </div>
                      <div>
                        <label style={{ ...labelSt, color: ERROR_TEXT }}>{t.fRagRed} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                        <input
                          type="text" value={form.ragRed}
                          onChange={e => { setF('ragRed', e.target.value); clearErr('ragRed'); }}
                          style={inputSt(formErrors.ragRed)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Section B — Relationships ── */}
            <div style={cardSt}>
              <div style={sectionHeading}>{t.sectionB}</div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>
                  {t.fObjective}
                  <InfoBadge text={t.ttObjective} />
                </label>
                <EntitySelector
                  items={objectiveItems}
                  selectedIds={form.objectiveId ? [form.objectiveId] : []}
                  onAdd={(id) => setF('objectiveId', id)}
                  onRemove={() => setF('objectiveId', null)}
                  placeholder={t.objPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.fLinkedControls}</label>
                <EntitySelector
                  items={controlItems}
                  selectedIds={form.linkedControlIds}
                  onAdd={(id) => setF('linkedControlIds', [...form.linkedControlIds, id])}
                  onRemove={(id) => setF('linkedControlIds', form.linkedControlIds.filter(x => x !== id))}
                  placeholder={t.ctrlPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                />
              </div>

              <div>
                <label style={labelSt}>
                  {t.fLinkedRisks}
                  <InfoBadge text={t.ttLinkedRisks} />
                </label>
                <EntitySelector
                  items={riskItems}
                  selectedIds={form.linkedRiskIds}
                  onAdd={(id) => setF('linkedRiskIds', [...form.linkedRiskIds, id])}
                  onRemove={(id) => setF('linkedRiskIds', form.linkedRiskIds.filter(x => x !== id))}
                  placeholder={t.riskPlaceholder}
                  disabled={isView || formSaveState === 'saving'}
                />
              </div>
            </div>

            {/* ── Section C — Readings ── */}
            <div style={cardSt}>
              <div style={sectionHeading}>{t.sectionC}</div>

              {sortedReadings.length === 0 ? (
                <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, marginTop: 0 }}>
                  {t.readingsEmpty}
                </p>
              ) : (
                <div style={{ overflowX: 'auto', marginBottom: '1.25rem', borderRadius: 8, border: `1px solid ${STATUS_NEUTRAL.bg}` }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thSt}>{t.colReadingDate}</th>
                        <th style={thSt}>{t.colReadingValue}</th>
                        <th style={thSt}>{t.colReadingStatus}</th>
                        <th style={thSt}>{t.colReadingEvidence}</th>
                        <th style={thSt}>{t.colReadingNotes}</th>
                        {!isView && <th style={{ ...thSt, width: 70 }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedReadings.map((r, i) => (
                        <tr key={i}>
                          <td style={{ ...tdSt, ...FONT_LABEL, whiteSpace: 'nowrap' }}>{r.date}</td>
                          <td style={{ ...tdSt, ...FONT_LABEL }}>
                            {r.value}{form.unit ? ` ${form.unit}` : ''}
                          </td>
                          <td style={tdSt}><ReadingStatusBadge status={r.status} t={t} /></td>
                          <td style={{ ...tdSt, ...FONT_SMALL, color: TEXT_MUTED }}>{renderEvidenceCell(r)}</td>
                          <td style={{ ...tdSt, fontSize: '0.8rem', color: TEXT_MUTED }}>{r.notes || '—'}</td>
                          {!isView && (
                            <td style={tdSt}>
                              <button
                                type="button"
                                onClick={() => setF('readings', form.readings.filter(x => x !== r))}
                                style={{
                                  ...BTN_DESTRUCTIVE_OUTLINE,
                                  ...FONT_SMALL,
                                  borderRadius: 5, padding: '0.15rem 0.45rem',
                                  cursor: 'pointer',
                                }}
                              >{t.deleteReadingBtn}</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add reading inline form */}
              {!isView && (
                <div>
                  <div style={{
                    ...FONT_TINY, fontSize: '0.68rem',
                    color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase',
                    marginBottom: '0.65rem',
                  }}>{t.addReadingTitle}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <label style={{ ...labelSt, marginBottom: 0 }}>{t.fReadingDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                      <input
                        type="date" value={form.newReadingDate}
                        onChange={e => setForm(prev => ({ ...prev, newReadingDate: e.target.value }))}
                        style={{ ...inputSt(false), width: 160 }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <label style={{ ...labelSt, marginBottom: 0 }}>{t.fReadingValue} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                      <input
                        type="text" value={form.newReadingValue}
                        onChange={e => setForm(prev => ({ ...prev, newReadingValue: e.target.value }))}
                        placeholder={form.unit ? `(${form.unit})` : ''}
                        style={{ ...inputSt(false), width: 120 }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <label style={{ ...labelSt, marginBottom: 0 }}>{t.fReadingStatus}</label>
                      <select
                        value={form.newReadingStatus}
                        onChange={e => setForm(prev => ({ ...prev, newReadingStatus: e.target.value }))}
                        style={{ ...inputSt(false), width: 140 }}
                      >
                        {READING_STATUSES.map(s => (
                          <option key={s} value={s}>
                            {s === 'Met' ? t.statusMet : s === 'Partial' ? t.statusPartial : t.statusNotMet}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      <label style={{ ...labelSt, marginBottom: 0 }}>{t.fReadingEvidenceId}</label>
                      <EvidenceSelector
                        orgId={org.id}
                        lang={lang}
                        value={form.newReadingEvidenceId || null}
                        onChange={(id) => setForm(prev => ({ ...prev, newReadingEvidenceId: id || '' }))}
                        multiple={false}
                        density="compact"
                        disabled={false}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: 140 }}>
                      <label style={{ ...labelSt, marginBottom: 0 }}>{t.fReadingNotes}</label>
                      <input
                        type="text" value={form.newReadingNotes}
                        onChange={e => setForm(prev => ({ ...prev, newReadingNotes: e.target.value }))}
                        style={inputSt(false)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddReading}
                      style={{
                        ...BTN_PRIMARY, ...FONT_LABEL, border: 'none',
                        borderRadius: 6, padding: '0.45rem 0.85rem', cursor: 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >{t.addReadingBtn}</button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </fieldset>
        <Divider />

        {/* Sticky action bar */}
        {!isView && (
          <div style={{
            position: 'sticky', bottom: 0,
            marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
            marginTop: '1rem',
            padding: '0.85rem 1.5rem',
            background: BG_SURFACE, borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div>
                {editingId !== null && (
                  <button
                    onClick={handleDeleteFromForm}
                    disabled={formSaveState === 'saving'}
                    style={{
                      ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, fontSize: '0.75rem',
                      borderRadius: 8,
                      padding: '0.5rem 1rem',
                      cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                    }}
                  >{t.deleteFormBtn}</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {dirty && (
                  <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
                    {t.unsavedLabel}
                  </span>
                )}
                <button
                  onClick={handleFormCancel}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_SECONDARY, ...FONT_BUTTON, fontWeight: 400, fontSize: '0.88rem',
                    borderRadius: 8, padding: '0.6rem 1.25rem',
                    cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                  }}
                >{t.formCancel}</button>
                <button
                  onClick={handleFormSave}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON, fontWeight: 700, fontSize: '0.88rem',
                    background: formSaveState === 'saving' ? TEXT_MUTED
                      : formSaveState === 'ok' ? BRAND_PRIMARY_DARK
                      : BTN_PRIMARY.background,
                    border: 'none', borderRadius: 8, padding: '0.6rem 1.6rem',
                    cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {formSaveState === 'saving' ? t.saving
                    : formSaveState === 'ok' ? t.saveOk
                    : t.formSave}
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn}
          cancelLabel={t.formCancel}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE, fontSize: '1.4rem',
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>
        <button
          onClick={openNew}
          style={{
            ...BTN_PRIMARY, ...FONT_BUTTON, fontSize: '0.85rem',
            border: 'none',
            borderRadius: 8, padding: '0.45rem 1rem', cursor: 'pointer',
          }}
        >{t.newBtn}</button>
      </div>

      {/* Delete error */}
      {deleteError && (
        <div style={{
          ...FONT_LABEL, fontSize: '0.82rem',
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1rem',
          color: ERROR_TEXT,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {deleteError}
          <button onClick={() => setDeleteError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ERROR_TEXT, fontSize: '1rem', padding: '0 0 0 0.75rem', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        {[
          { label: t.cntTotal,   value: total,        color: TEXT_DEFAULT },
          { label: t.cntMet,     value: metCount,     color: BRAND_PRIMARY_DARK },
          { label: t.cntAtRisk,  value: atRiskCount,  color: STATUS_WARN.text },
          { label: t.cntNotMet,  value: notMetCount,  color: ERROR_TEXT },
          { label: t.cntNoData,  value: noDataCount,  color: TEXT_DISABLED },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
            borderRadius: 10, padding: '0.9rem 1.25rem',
            flex: 1, minWidth: 88,
          }}>
            <div style={{ ...FONT_SMALL, color: TEXT_DEFAULT, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>{label}</div>
            <div style={{ ...FONT_STAT_VALUE, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      {metrics.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterFrequency}</span>
            <select
              value={filterFreq}
              onChange={e => setFilterFreq(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}
            >
              <option value="">{t.filterAll}</option>
              {FREQUENCIES.map(f => (<option key={f} value={f}>{freqLabel(f)}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterRag}</span>
            <select
              value={filterRag}
              onChange={e => setFilterRag(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}
            >
              <option value="">{t.filterAll}</option>
              <option value="green">{t.ragGreen}</option>
              <option value="amber">{t.ragAmber}</option>
              <option value="red">{t.ragRed}</option>
              <option value="none">{t.ragNoData}</option>
            </select>
          </div>
        </div>
      )}

      {/* Empty state */}
      {metrics.length === 0 && (
        <div style={{ background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10, padding: '3rem', textAlign: 'center' }}>
          <p style={{ ...FONT_BODY, fontSize: '1rem', color: TEXT_MUTED, marginBottom: '0.5rem', marginTop: 0 }}>{t.emptyTitle}</p>
          <p style={{ ...FONT_LABEL, fontSize: '0.8rem', color: TEXT_DISABLED, marginBottom: '1.5rem', marginTop: 0 }}>{t.emptyHint}</p>
          <button
            onClick={openNew}
            style={{ ...BTN_PRIMARY, ...FONT_BUTTON, fontSize: '0.9rem', border: 'none', borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer' }}
          >{t.newBtn}</button>
        </div>
      )}

      {/* Table */}
      {metrics.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, minWidth: 100 }}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 200 }}>{t.colName}</th>
                <th style={{ ...thSt, minWidth: 100 }}>{t.colFrequency}</th>
                <th style={{ ...thSt, minWidth: 120 }}>{t.colResponsible}</th>
                <th style={{ ...thSt, minWidth: 120 }}>{t.colTarget}</th>
                <th style={{ ...thSt, minWidth: 160 }}>{t.colLatestReading}</th>
                <th style={{ ...thSt, minWidth: 100 }}>{t.colRag}</th>
                <th style={{ ...thSt, minWidth: 130 }}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.map(met => {
                const latest = getLatestReading(met);
                const rag = computeRagStatus(met);
                return (
                  <tr
                    key={met.id}
                    onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                  >
                    <td style={{ ...tdSt, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>{met.ref}</td>
                    <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING, maxWidth: 240 }}>{met.name}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL }}>{freqLabel(met.frequency)}</td>
                    <td style={{ ...tdSt, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{resolvePerson(met.responsible, people) || '—'}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL }}>{met.target}</td>
                    <td style={{ ...tdSt, ...FONT_LABEL }}>
                      {latest ? `${latest.value}${met.unit ? ` ${met.unit}` : ''} (${latest.date})` : '—'}
                    </td>
                    <td style={tdSt}><RagBadge rag={rag} t={t} /></td>
                    <td style={tdSt}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button
                          onClick={() => openView(met)}
                          style={{ ...BTN_SECONDARY, ...FONT_SMALL, fontSize: '0.75rem', borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                        >{t.viewBtn}</button>
                        <button
                          onClick={() => openEdit(met)}
                          style={{ ...BTN_SECONDARY, ...FONT_SMALL, fontSize: '0.75rem', borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                        >{t.editBtn}</button>
                        <button
                          onClick={() => handleDeleteOne(met)}
                          style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, fontSize: '0.75rem', borderRadius: 5, padding: '0.2rem 0.6rem', cursor: 'pointer' }}
                        >{t.deleteBtn}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Divider />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn}
        cancelLabel={t.formCancel}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
